'use client';

import React, { useEffect, useRef, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { usePathname, useRouter } from '@/i18n/navigation';
import {
  LOCALE_COOKIE,
  LOCALE_COOKIE_MAX_AGE,
  localeNames,
  locales,
  type Locale,
} from '@/i18n/routing';

/** Discreet "ES ▾" language menu for the header. A manual choice is remembered in a cookie. */
export default function LanguageSwitcher() {
  const t = useTranslations('header');
  const locale = useLocale() as Locale;
  const router = useRouter();
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const choose = (next: Locale) => {
    document.cookie = `${LOCALE_COOKIE}=${next}; path=/; max-age=${LOCALE_COOKIE_MAX_AGE}; samesite=lax`;
    setOpen(false);
    if (next !== locale) router.replace(pathname, { locale: next, scroll: false });
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={`${t('language')}: ${localeNames[locale]}`}
        className="label-mono h-9 flex items-center gap-2 text-[rgba(242,235,224,0.72)] hover:text-[var(--cream)] transition-colors duration-300"
      >
        {locale.toUpperCase()}
        <span
          aria-hidden="true"
          className={`inline-block w-[5px] h-[5px] border-r border-b border-current transition-transform duration-300 ${
            open ? 'rotate-[225deg] translate-y-[1px]' : 'rotate-45 -translate-y-[2px]'
          }`}
        />
      </button>

      <ul
        className={`absolute right-0 top-full mt-2 min-w-[max(10rem,140px)] py-2 bg-[rgba(11,9,7,0.95)] backdrop-blur-xl border border-[var(--line)] transition-[opacity,visibility,transform] duration-300 ${
          open ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-1'
        }`}
      >
        {locales.map((l) => (
          <li key={l}>
            <button
              type="button"
              lang={l}
              onClick={() => choose(l)}
              aria-current={l === locale ? 'true' : undefined}
              tabIndex={open ? 0 : -1}
              className={`w-full flex items-center justify-between gap-5 px-4 py-2 label-mono transition-colors duration-300 ${
                l === locale
                  ? 'text-copper'
                  : 'text-[rgba(242,235,224,0.72)] hover:text-[var(--cream)]'
              }`}
            >
              <span>{l.toUpperCase()}</span>
              <span className="normal-case tracking-normal opacity-70">{localeNames[l]}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
